import React, { useState, useEffect, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import queryString from 'query-string'
import jwtDecode from 'jwt-decode'

// Hooks
import useOktaVerifier, { VALIDATION_STATUS } from './hooks/useOktaVerifier'

// Utils
import loSto from '../../config/loSto'

// Helpers
import { getEmbedOktaAuthInstance } from './helpers/withEmbedAuthentication.okta'
import {
  getJwtFromAuthToken,
  getEmbedUserPassword,
  initialiseEmbedClientSetup,
  cleanEmbedClientSetup
} from './helpers/withEmbedAuthentication.general'

// Services
import { getEmbedUser } from '../../rest/embedUser'

// Readers
import embedUserReader from './readers/EmbedUser'

// Constants
import { LO_STO } from '../../lib'
import { EMBED_SOURCE } from './constants/withEmbedAuthentication.general'
import OKTACONFIG from './constants/withEmbedAuthentication.okta'

// Context
import { SukiContext } from '../../core/SukiContext'

const withEmbedAuthentication = (WrappedComponent) => {
  return (props) => {
    const history = useHistory()
    const location = useLocation()
    const [isEmbed, setIsEmbed] = useState(false)
    const [, setTheme] = useContext(SukiContext)

    const params = queryString.parse(location.search) || {}
    const {
      src,
      tenantName,
      token: authToken,
      appointmentId,
      suki_mic_enabled: sukiMicEnabled,
      pagetype,
      view
    } = params
    const jwt = getJwtFromAuthToken(authToken)
    const { isValid: isValidSession, validationStatus, validateJwtToken } = useOktaVerifier(tenantName)

    const signInToOkta = async () => {
      const oktaAuth = getEmbedOktaAuthInstance(tenantName)
      const { partnerId } = jwtDecode(jwt)

      // Fetch the embed user details to login to Okta
      getEmbedUser(partnerId, tenantName, jwt)
        .then((embedUserDetails) => {
          oktaAuth
            .signIn({
              username: embedUserReader.oktaUsername(embedUserDetails),
              password: getEmbedUserPassword(embedUserDetails)
            })
            .then((res) => {
              // Set the session token in localstorage and trigger okta redirect
              loSto.set(LO_STO.SESSION_ID, res.sessionToken)
              oktaAuth.token.getWithRedirect({
                responseType: OKTACONFIG.RESPONSE_TYPE,
                responseMode: OKTACONFIG.RESPONSE_MODE,
                sessionToken: res.sessionToken,
                scopes: OKTACONFIG.SCOPES
              })
            })
            .catch(() => {
              history.push('/')
            })
        })
        .catch(() => {
          history.push('/')
        })
    }

    useEffect(() => {
      initialiseEmbedClientSetup({
        tenantName,
        sukiMicEnabled,
        pagetype,
        appointmentId,
        view
      }, setTheme)
      return () => {
        cleanEmbedClientSetup()
      }
    }, [tenantName, sukiMicEnabled, pagetype, setTheme, view])

    useEffect(() => {
      if (validationStatus === VALIDATION_STATUS.COMPLETE) {
        if (isValidSession) {
          // If the jwt token is valid, sign in to okta
          signInToOkta()
        } else {
          // If validation fails, redirect to home page
          history.push('/')
        }
      }
    }, [isValidSession, validationStatus])

    useEffect(() => {
      if (src === EMBED_SOURCE) {
        validateJwtToken(jwt)
        setIsEmbed(true)
      } else {
        setIsEmbed(false)
      }
    }, [src, jwt])

    if (isEmbed) {
      return null
    }

    return <WrappedComponent {...props} />
  }
}

export default withEmbedAuthentication
